import type { DeskAnalysis, LastRun, SectorDesk } from 'api/sectorAlignmentApi';
import { sameSet } from './deskSelection';

/** 'selection' = the selector's analysis (replaces the result set); 'desk' = one desk re-read (retry, after its job). */
export type Scope = 'selection' | 'desk';

export interface PageState {
  selected: SectorDesk[] | null; // null until the desk list arrives
  restored: boolean;
  desks: DeskAnalysis[];
  analyzing: { sd: SectorDesk; scope: Scope }[]; // one entry per desk per request in flight
  lastRun: LastRun;
  justFinished: SectorDesk | null;
  realignDesk: SectorDesk | null;
  realignError: string | null;
  now: number;
}

export type Action =
  | { type: 'init'; selected: SectorDesk[]; restored: boolean }
  | { type: 'select'; sds: SectorDesk[] }
  | { type: 'analyze'; sds: SectorDesk[]; scope: Scope }
  | { type: 'analyzed'; sds: SectorDesk[]; scope: Scope; desks: DeskAnalysis[]; lastRun?: LastRun }
  | { type: 'finished'; sd: SectorDesk }
  | { type: 'openRealign'; sd: SectorDesk | null }
  | { type: 'realignError'; message: string }
  | { type: 'tick'; now: number };

export const initialState = (now: number): PageState => ({
  selected: null, restored: false, desks: [], analyzing: [], lastRun: null, justFinished: null, realignDesk: null, realignError: null, now,
});

export const reducer = (state: PageState, action: Action): PageState => {
  switch (action.type) {
    case 'init':
      return state.selected ? state : { ...state, selected: action.selected, restored: action.restored };
    case 'select':
      return { ...state, selected: action.sds, restored: false };
    case 'analyze': {
      const entries = action.sds.map((sd) => ({ sd, scope: action.scope }));
      return { ...state, analyzing: [...state.analyzing, ...entries], justFinished: action.scope === 'selection' ? null : state.justFinished };
    }
    case 'analyzed': {
      // AC13: only the requested desks are filled; a selection analysis drops results of desks no longer selected.
      const fresh = action.desks.filter((d) => action.sds.includes(d.sd));
      const kept = state.desks.filter(
        (d) => !action.sds.includes(d.sd) && (action.scope === 'desk' || (state.selected ?? []).includes(d.sd))
      );
      const analyzing = action.sds.reduce((left, sd) => {
        const i = left.findIndex((e) => e.sd === sd && e.scope === action.scope);
        return i < 0 ? left : [...left.slice(0, i), ...left.slice(i + 1)];
      }, state.analyzing);
      return { ...state, desks: [...kept, ...fresh], lastRun: action.lastRun === undefined ? state.lastRun : action.lastRun, analyzing };
    }
    case 'finished':
      return { ...state, justFinished: action.sd };
    case 'openRealign':
      return { ...state, realignDesk: action.sd, realignError: null };
    case 'realignError':
      return { ...state, realignError: action.message };
    case 'tick':
      return { ...state, now: action.now };
    default:
      return state;
  }
};

const selectedOf = (state: PageState) => state.selected ?? [];
const resultOf = (state: PageState, sd: SectorDesk) => state.desks.find((d) => d.sd === sd);

/** B5: the selection differs from what was (or is being) analysed. */
export const selectionChanged = (state: PageState): boolean => {
  const requested = state.analyzing.filter((e) => e.scope === 'selection').map((e) => e.sd);
  const target = requested.length ? requested : state.desks.map((d) => d.sd);
  return target.length > 0 && !sameSet(selectedOf(state), Array.from(new Set(target)));
};

export type DeskView = 'skeleton' | 'refreshing' | 'result' | 'pending';

/** AC12: a selection relaunch keeps the result (indicator); a single-desk re-read hides it behind a skeleton. */
export const deskView = (state: PageState, sd: SectorDesk): DeskView => {
  const entry = state.analyzing.find((e) => e.sd === sd);
  const result = resultOf(state, sd);
  if (entry) return result && entry.scope === 'selection' ? 'refreshing' : 'skeleton';
  return result ? 'result' : 'pending';
};

/** Cards follow the selection once anything is analysed; before that, only the selected desk whose job runs (AC20). */
export const cardDesks = (state: PageState, runningSd: SectorDesk | null | undefined): SectorDesk[] =>
  state.desks.length || state.analyzing.length ? selectedOf(state) : selectedOf(state).filter((sd) => sd === runningSd);

/** R-208: a desk re-read after its job leaves the tabs, its old rows would read as current. */
export const tabDesks = (state: PageState): DeskAnalysis[] =>
  selectedOf(state).flatMap((sd) => {
    const result = resultOf(state, sd);
    return result && !state.analyzing.some((e) => e.sd === sd && e.scope === 'desk') ? [result] : [];
  });

export const allAligned = (state: PageState): boolean =>
  state.analyzing.length === 0 &&
  selectedOf(state).length > 0 &&
  selectedOf(state).every((sd) => {
    const result = resultOf(state, sd);
    return result?.status === 'ok' && result.misaligned <= 0;
  });
